import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, BookOpen, Heart, Youtube, Mail, Phone, MapPin, Send, Cross, Music, Star, Bird, Crown, Shield, Sun, Moon, Feather, X, Clock, Eye } from 'lucide-react';
import Navigation from './Navigation';
import Footer from './Footer';
import SEOHead from './SEOHead';

const LandingPage = () => {
  const [prayerRequest, setPrayerRequest] = useState('');
  const [email, setEmail] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [youtubeEpisodes, setYoutubeEpisodes] = useState([]);
  const [loadingEpisodes, setLoadingEpisodes] = useState(true);
  const [episodesError, setEpisodesError] = useState(null);

  // Fetch YouTube episodes
  useEffect(() => {
    const fetchEpisodes = async () => {
      try {
        setEpisodesError(null);
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/youtube/episodes?max_results=6`);
        if (response.ok) {
          const data = await response.json();
          setYoutubeEpisodes(data.episodes || []);
        } else {
          console.error('Nu s-au putut încărca episoadele');
          setEpisodesError('Nu s-au putut încărca episoadele în acest moment');
        }
      } catch (error) {
        console.error('Eroare la încărcarea episoadelor:', error);
        setEpisodesError('Eroare de conexiune. Episoadele vor fi afișate când conexiunea se va restabili.');
      } finally {
        setLoadingEpisodes(false);
      }
    };

    fetchEpisodes();
  }, []);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": ["WebSite", "PodcastSeries"],
    "name": "Cuvânt și Rugăciune",
    "alternateName": "Podcast Creștin cu Mircea Stanciu",
    "description": "Descoperă puterea transformatoare a Cuvântului lui Dumnezeu prin podcasturi inspiraționale și studii biblice profunde cu Mircea Stanciu",
    "url": "https://cuvantsirugaciune.ro",
    "author": {
      "@type": "Person",
      "@id": "https://cuvantsirugaciune.ro/despre#mircea-stanciu",
      "name": "Mircea Stanciu",
      "description": "Pasionat de studiul Scripturii și devotat în slujirea comunității creștine"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Cuvânt și Rugăciune",
      "logo": {
        "@type": "ImageObject",
        "url": "https://cuvantsirugaciune.ro/logo.png"
      }
    },
    "genre": "Religion & Spirituality",
    "inLanguage": "ro",
    "keywords": ["podcast creștin", "studii biblice", "rugăciune", "spiritualitate creștină", "Biblie", "România"],
    "audience": {
      "@type": "Audience",
      "audienceType": "Creștini români"
    },
    "potentialAction": {
      "@type": "WatchAction",
      "target": "https://www.youtube.com/@mircea_stanciu"
    }
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, ease: "easeOut" }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const handlePrayerSubmit = (e) => {
    e.preventDefault();
    // Aici va fi implementată logica pentru trimiterea cererii de rugăciune
    alert('Cererea ta de rugăciune a fost trimisă. Dumnezeu te binecuvânteze!');
    setPrayerRequest('');
  };

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    // Aici va fi implementată logica pentru newsletter
    alert('Te-ai înscris cu succes! Vei primi notificări despre noile episoade.');
    setEmail('');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ro-RO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatViewCount = (count) => {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`;
    } else if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    }
    return count.toString();
  };

  return (
    <div className="bg-slate-900 text-white overflow-hidden">
      <SEOHead 
        title="Cuvânt și Rugăciune cu Mircea Stanciu - Podcast Creștin Român"
        description="Descoperă puterea transformatoare a Cuvântului lui Dumnezeu prin podcasturi inspiraționale și studii biblice profunde cu Mircea Stanciu. Spiritualitate creștină autentică pentru sufletul tău."
        keywords="podcast crestin, mircea stanciu, studii biblice, rugaciune, spiritualitate crestina, biblie romania, meditatii crestine, invatatura biblica"
        canonicalUrl="/"
        ogTitle="Cuvânt și Rugăciune - Podcast Creștin cu Mircea Stanciu"
        ogDescription="Alătură-te călătoriei spirituale prin Scripturi cu Mircea Stanciu. Episoade inspiraționale, studii biblice și rugăciune pentru creștinii români."
        structuredData={structuredData}
      />
      
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.7), rgba(15, 23, 42, 0.8)), url('https://images.unsplash.com/photo-1617610882105-71c5d7447837')`
          }}
        />
        
        {/* Floating Christian symbols */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{ y: [0, -20, 0], rotate: [0, 5, 0] }}
            transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
            className="absolute top-20 left-10 text-amber-400/20"
          >
            <Cross className="w-8 h-8" />
          </motion.div>
          <motion.div
            animate={{ y: [0, 15, 0], rotate: [0, -3, 0] }}
            transition={{ repeat: Infinity, duration: 5, ease: "easeInOut", delay: 1 }}
            className="absolute top-32 right-16 text-amber-400/20"
          >
            <Bird className="w-6 h-6" />
          </motion.div>
          <motion.div
            animate={{ y: [0, -10, 0], rotate: [0, 2, 0] }}
            transition={{ repeat: Infinity, duration: 6, ease: "easeInOut", delay: 2 }}
            className="absolute bottom-32 left-20 text-amber-400/20"
          >
            <Star className="w-5 h-5" />
          </motion.div>
          <motion.div
            animate={{ y: [0, 12, 0], rotate: [0, -4, 0] }}
            transition={{ repeat: Infinity, duration: 3.5, ease: "easeInOut", delay: 0.5 }}
            className="absolute bottom-40 right-12 text-amber-400/20"
          >
            <Crown className="w-7 h-7" />
          </motion.div>
        </div>
        
        <div className="relative container mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="max-w-4xl mx-auto"
          >
            <div className="flex justify-center items-center space-x-4 mb-6">
              <Shield className="w-8 h-8 sm:w-12 sm:h-12 text-amber-400" />
              <Cross className="w-10 h-10 sm:w-16 sm:h-16 text-amber-400" />
              <Bird className="w-8 h-8 sm:w-12 sm:h-12 text-amber-400" />
            </div>
            
            <h1 className="text-4xl sm:text-6xl md:text-8xl font-bold mb-4 sm:mb-6 bg-gradient-to-r from-amber-400 via-amber-300 to-amber-500 bg-clip-text text-transparent leading-tight">
              Cuvânt și Rugăciune
            </h1>
            
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className="text-lg sm:text-xl md:text-2xl text-slate-300 mb-3 sm:mb-4 leading-relaxed px-4 relative z-10"
              style={{opacity: 1}}
            >
              Descoperă puterea transformatoare a Cuvântului lui Dumnezeu prin podcasturi inspiraționale și studii biblice profunde
            </motion.p>
            
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.8 }}
              className="text-base sm:text-lg text-amber-400 mb-6 sm:mb-8 font-medium"
            >
              cu Mircea Stanciu
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.8 }}
              className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center px-4"
            >
              <button 
                onClick={() => {
                  if (youtubeEpisodes.length > 0) {
                    window.open(youtubeEpisodes[0].url, '_blank');
                  } else {
                    window.open('https://www.youtube.com/@mircea_stanciu', '_blank');
                  }
                }} 
                className="w-full sm:w-auto bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-slate-900 font-bold py-3 sm:py-4 px-6 sm:px-8 rounded-2xl transition-all duration-300 transform hover:scale-105 hover:shadow-2xl flex items-center justify-center space-x-2"
              >
                <Play className="w-5 h-5" />
                <span>Ascultă Primul Episod</span>
              </button>
              
              <button onClick={() => window.open('https://www.youtube.com/@mircea_stanciu', '_blank')} className="w-full sm:w-auto border-2 border-amber-400 text-amber-400 hover:bg-amber-400 hover:text-slate-900 font-bold py-3 sm:py-4 px-6 sm:px-8 rounded-2xl transition-all duration-300 transform hover:scale-105 flex items-center justify-center space-x-2">
                <Youtube className="w-5 h-5" />
                <span>Vezi pe YouTube</span>
              </button>
            </motion.div>
          </motion.div>
        </div>
        
        <motion.div
          animate={{ y: [0, -10, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        >
          <div className="w-1 h-12 sm:h-16 bg-gradient-to-b from-amber-400 to-transparent rounded-full"></div>
        </motion.div>
      </section>

      {/* Despre Section */}
      <section id="despre" className="py-16 sm:py-20 bg-slate-800/50 relative">
        {/* Decorative symbols */}
        <div className="absolute top-10 left-4 text-amber-400/10">
          <Sun className="w-16 h-16" />
        </div>
        <div className="absolute bottom-10 right-4 text-amber-400/10">
          <Moon className="w-12 h-12" />
        </div>
        
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-center"
          >
            <motion.div variants={fadeInUp} className="order-2 lg:order-1">
              <div className="flex items-center space-x-3 mb-4">
                <Heart className="w-8 h-8 text-amber-400" />
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-amber-400">Despre Podcast</h2>
              </div>
              <p className="text-base sm:text-lg text-slate-300 mb-4 sm:mb-6 leading-relaxed">
                "Cuvânt și Rugăciune" este o călătorie spirituală prin Scripturi, 
                unde explorăm împreună înțelepciunea veche și relevanța ei pentru viața noastră de astăzi.
              </p>
              <p className="text-base sm:text-lg text-slate-300 mb-6 leading-relaxed">
                Fiecare episod combină studiul biblic profund cu rugăciunea contemplativă, 
                oferind hrană spirituală pentru sufletul însetat de adevăr.
              </p>
              
              <div className="bg-slate-700/50 p-4 sm:p-6 rounded-2xl border border-amber-400/20">
                <div className="flex items-center space-x-2 mb-3">
                  <Crown className="w-6 h-6 text-amber-400" />
                  <h3 className="text-lg sm:text-xl font-semibold text-amber-400">Mircea Stanciu</h3>
                </div>
                <p className="text-sm sm:text-base text-slate-300 leading-relaxed">
                  Pasionat de studiul Scripturii și devotat în slujirea comunității creștine, 
                  împărtășesc cu dragoste și înțelepciune bogățiile Cuvântului lui Dumnezeu 
                  prin aceste podcasturi inspiraționale.
                </p>
              </div>
            </motion.div>
            
            <motion.div 
              variants={fadeInUp}
              className="relative order-1 lg:order-2"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-amber-400/20 to-transparent rounded-3xl"></div>
              <img 
                src="https://images.pexels.com/photos/18653022/pexels-photo-18653022.jpeg"
                alt="Podcast Setup"
                className="w-full h-64 sm:h-80 lg:h-96 object-cover rounded-3xl shadow-2xl"
              />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Episoade Section */}
      <section id="episoade" className="py-16 sm:py-20 relative">
        {/* Decorative cross pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="grid grid-cols-6 sm:grid-cols-8 gap-8 p-8">
            {[...Array(48)].map((_, i) => (
              <Cross key={i} className="w-4 h-4 text-amber-400" />
            ))}
          </div>
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 relative">
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12 sm:mb-16"
          >
            <div className="flex justify-center items-center space-x-3 mb-4">
              <Music className="w-8 h-8 text-amber-400" />
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-amber-400">Episoade Recente</h2>
              <Music className="w-8 h-8 text-amber-400" />
            </div>
            <p className="text-lg sm:text-xl text-slate-300 max-w-3xl mx-auto px-4">
              Descoperă cele mai noi episoade pline de învățătură biblică și înțelepciune spirituală
            </p>
          </motion.div>
          
          {loadingEpisodes ? (
            <div className="text-center">
              <div className="inline-flex items-center space-x-2">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-amber-400"></div>
                <span className="text-slate-300">Se încarcă episoadele...</span>
              </div>
            </div>
          ) : (
            <motion.div
              variants={staggerContainer}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8"
            >
              {youtubeEpisodes.length > 0 ? youtubeEpisodes.slice(0, 6).map((episode, index) => (
                <motion.div
                  key={episode.id}
                  variants={fadeInUp}
                  className="bg-slate-800/70 rounded-3xl overflow-hidden border border-amber-400/20 hover:border-amber-400/40 transition-all duration-300 group hover:transform hover:scale-105 cursor-pointer"
                  onClick={() => window.open(episode.url, '_blank')}
                >
                  <div className="relative h-40 sm:h-48 overflow-hidden">
                    <img 
                      src={episode.thumbnail_url} 
                      alt={episode.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    <div className="absolute top-2 left-2 bg-amber-400 text-slate-900 px-2 py-1 rounded-lg text-xs font-semibold">
                      Episod {index + 1}
                    </div>
                    <div className="absolute top-2 right-2 bg-black/70 text-white px-2 py-1 rounded-lg text-xs flex items-center space-x-1">
                      <Clock className="w-3 h-3" />
                      <span>{episode.duration}</span>
                    </div>
                    <div className="absolute bottom-2 right-2">
                      <Play className="w-8 h-8 text-amber-400 bg-black/70 rounded-full p-2" />
                    </div>
                  </div>
                  <div className="p-4 sm:p-6">
                    <h3 className="text-lg sm:text-xl font-semibold text-amber-400 mb-3 line-clamp-2">
                      {episode.title}
                    </h3>
                    <p className="text-sm sm:text-base text-slate-300 mb-4 line-clamp-3">
                      {episode.description || 'Alătură-te acestei călătorii spirituale prin Cuvântul lui Dumnezeu. Un episod plin de învățătură biblică și înțelepciune spirituală pentru sufletul tău.'}
                    </p>
                    <div className="flex items-center justify-between text-xs text-slate-400">
                      <span>{formatDate(episode.published_at)}</span>
                      <div className="flex items-center space-x-1">
                        <Eye className="w-3 h-3" />
                        <span>{formatViewCount(episode.view_count)}</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )) : (
                // Fallback content when no episodes are available or loading failed
                [...Array(6)].map((_, index) => (
                  <motion.div
                    key={index}
                    variants={fadeInUp}
                    className="bg-slate-800/70 rounded-3xl overflow-hidden border border-amber-400/20 hover:border-amber-400/40 transition-all duration-300 group hover:transform hover:scale-105"
                  >
                    <div className="h-40 sm:h-48 bg-gradient-to-br from-amber-500/20 to-slate-700 flex items-center justify-center relative">
                      <Music className="w-12 h-12 sm:w-16 sm:h-16 text-amber-400" />
                      <div className="absolute top-2 right-2">
                        <Star className="w-4 h-4 text-amber-300" />
                      </div>
                    </div>
                    <div className="p-4 sm:p-6">
                      <h3 className="text-lg sm:text-xl font-semibold text-amber-400 mb-3">
                        Episodul {index + 1}: În Curând
                      </h3>
                      <p className="text-sm sm:text-base text-slate-300 mb-4">
                        Un nou episod plin de învățătură și rugăciune va fi disponibil în curând. 
                        Abonează-te pentru a fi notificat!
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs sm:text-sm text-slate-400">În pregătire</span>
                        <button 
                          onClick={() => window.open('https://www.youtube.com/@mircea_stanciu', '_blank')}
                          className="text-amber-400 hover:text-amber-300 transition-colors"
                        >
                          <Play className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </motion.div>
          )}

          {/* Error message for episodes if needed */}
          {episodesError && !loadingEpisodes && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mt-8"
            >
              <div className="bg-slate-800/50 rounded-2xl p-6 border border-yellow-400/20 max-w-2xl mx-auto">
                <Star className="w-8 h-8 text-yellow-400 mx-auto mb-3" />
                <p className="text-yellow-400 text-sm mb-2">Notificare</p>
                <p className="text-slate-300 text-sm">{episodesError}</p>
              </div>
            </motion.div>
          )}

          {/* Link to all episodes */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12 sm:mt-16"
          >
            <a href="/episoade">
              <button className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-slate-900 font-bold py-3 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 flex items-center space-x-2 mx-auto">
                <Music className="w-5 h-5" />
                <span>Vezi Toate Episoadele</span>
              </button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* Rest of the component remains the same... */}
      {/* Studii Biblice Section */}
      <section id="studii" className="py-16 sm:py-20 bg-slate-800/50 relative">
        {/* Decorative elements */}
        <div className="absolute top-6 right-6 text-amber-400/10">
          <Shield className="w-20 h-20" />
        </div>
        <div className="absolute bottom-6 left-6 text-amber-400/10">
          <Crown className="w-16 h-16" />
        </div>
        
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -60 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative order-2 lg:order-1"
            >
              <img 
                src="https://images.pexels.com/photos/1029141/pexels-photo-1029141.jpeg"
                alt="Studiu Biblic"
                className="w-full h-64 sm:h-80 lg:h-96 object-cover rounded-3xl shadow-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 to-transparent rounded-3xl"></div>
              <div className="absolute top-4 left-4">
                <BookOpen className="w-8 h-8 text-amber-400" />
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 60 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <div className="flex items-center space-x-3 mb-4 sm:mb-6">
                <BookOpen className="w-8 h-8 sm:w-12 sm:h-12 text-amber-400" />
                <Star className="w-6 h-6 text-amber-300" />
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 text-amber-400">Studii Biblice</h2>
              <p className="text-base sm:text-lg text-slate-300 mb-6 leading-relaxed">
                Aprofundează-ți înțelegerea Scripturii prin studii biblice sistematice 
                și contemplarea profundă a Cuvântului lui Dumnezeu.
              </p>
              
              <div onClick={() => window.open('https://chat.whatsapp.com/JDVNz6oeuN9KQljJm7vxyY', '_blank')} className="space-y-3 sm:space-y-4">
                <div className="bg-slate-700/50 p-3 sm:p-4 rounded-xl border border-amber-400/20">
                  <div className="flex items-center space-x-2 mb-2">
                    <Sun className="w-5 h-5 text-amber-400" />
                    <h4 className="font-semibold text-amber-400">Studiul Săptămânal</h4>
                  </div>
                  <p className="text-sm sm:text-base text-slate-300">Explorarea temelor biblice cu aplicații practice pentru viața creștină</p>
                </div>
                
                <div onClick={() => window.open('https://chat.whatsapp.com/IIkw1I0fnbzKRH4RZin4ss', '_blank')} className="bg-slate-700/50 p-3 sm:p-4 rounded-xl border border-amber-400/20">
                  <div className="flex items-center space-x-2 mb-2">
                    <Moon className="w-5 h-5 text-amber-400" />
                    <h4 className="font-semibold text-amber-400">Meditații Zilnice</h4>
                  </div>
                  <p className="text-sm sm:text-base text-slate-300">Reflecții scurte și profunde pentru hrănirea spirituală zilnică</p>
                </div>

                  
                <div onClick={() => window.open('https://chat.whatsapp.com/L5Tj9gTBPsE5oBZTdrw0Yp', '_blank')} className="bg-slate-700/50 p-3 sm:p-4 rounded-xl border border-amber-400/20">
                  <div className="flex items-center space-x-2 mb-2">
                    <Crown className="w-5 h-5 text-amber-400" />
                    <h4 className="font-semibold text-amber-400">Studii Tematice</h4>
                  </div>
                  <p className="text-sm sm:text-base text-slate-300">Analize aprofundate ale marilor teme biblice și doctrinare</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Rugăciuni Section */}
      <section id="rugaciuni" className="py-16 sm:py-20 relative">
        {/* Decorative dove pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="grid grid-cols-8 gap-16 p-8">
            {[...Array(32)].map((_, i) => (
              <Bird key={i} className="w-6 h-6 text-amber-400" />
            ))}
          </div>
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 relative">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -60 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <div className="flex items-center space-x-3 mb-4 sm:mb-6">
                <Heart className="w-8 h-8 sm:w-12 sm:h-12 text-amber-400" />
                <Feather className="w-6 h-6 sm:w-8 sm:h-8 text-amber-300" />
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 text-amber-400">Rugăciuni și Cereri</h2>
              <p className="text-base sm:text-lg text-slate-300 mb-6 sm:mb-8 leading-relaxed">
                Împărtășește-ți nevoile și cererile de rugăciune. Suntem o comunitate care se roagă 
                împreună și se sprijină în credință.
              </p>
              
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target);
                  const message = formData.get('message');
                  const nume = formData.get('nume');
                  const prenume = formData.get('prenume');
                  const email = formData.get('email');
                  
                  // Mostra stato di caricamento
                  const submitBtn = e.target.querySelector('button[type="submit"]');
                  const originalText = submitBtn.innerHTML;
                  submitBtn.innerHTML = '<svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>Se trimite...';
                  submitBtn.disabled = true;
                  
                  try {
                    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/mailgun/prayer-request`, {
                      method: 'POST',
                      headers: { 
                        'Content-Type': 'application/json',
                      },
                      body: JSON.stringify({ 
                        name: nume || 'Vizitator anonim',
                        message: message,
                        email: email,
                        nume: nume,
                        prenume: prenume
                      }),
                    });

                    const result = await response.json();
                    
                    if (response.ok && result.success) {
                      // Messaggio di successo migliorato
                      const successMessage = result.sandbox_mode 
                        ? '✅ Cererea ta de rugăciune a fost primită! În versiunea finală, vei primi o confirmare prin email. Dumnezeu te binecuvânteze! 🙏'
                        : (result.message || 'Cererea ta de rugăciune a fost trimisă cu succes! Dumnezeu te binecuvânteze! 🙏');
                      
                      alert(successMessage);
                      e.target.reset();
                    } else {
                      // Messaggio di errore migliorato
                      const errorMessage = result.detail?.includes('sandbox') || result.message?.includes('sandbox')
                        ? '📝 Cererea ta a fost înregistrată! Momentan sistemul este în modul de testare, dar mesajul tău a fost salvat. Mulțumim pentru răbdare!'
                        : 'A apărut o problemă tehnică. Te rugăm să încerci din nou peste câteva minute.';
                      
                      alert(errorMessage);
                    }
                  } catch (error) {
                    console.error('Prayer request error:', error);
                    alert('📡 Problemă de conexiune. Te rugăm să încerci din nou peste câteva momente.');
                  } finally {
                    // Ripristina il pulsante
                    submitBtn.innerHTML = originalText;
                    submitBtn.disabled = false;
                  }
                }}
                className="space-y-4 sm:space-y-6"
              >
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1">
                    <label className="block text-amber-400 font-semibold mb-2 text-sm">
                      Nume (opțional):
                    </label>
                    <input
                      name="nume"
                      type="text"
                      placeholder="Numele tău"
                      className="w-full p-3 bg-slate-800/70 border border-amber-400/30 rounded-xl text-white placeholder-slate-400 focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-400/20 text-sm sm:text-base"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-amber-400 font-semibold mb-2 text-sm">
                      Prenume (opțional):
                    </label>
                    <input
                      name="prenume"
                      type="text"
                      placeholder="Prenumele tău"
                      className="w-full p-3 bg-slate-800/70 border border-amber-400/30 rounded-xl text-white placeholder-slate-400 focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-400/20 text-sm sm:text-base"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-amber-400 font-semibold mb-2 text-sm">
                    Email (opțional):
                  </label>
                  <input
                    name="email"
                    type="email"
                    placeholder="email@exemplu.com"
                    className="w-full p-3 bg-slate-800/70 border border-amber-400/30 rounded-xl text-white placeholder-slate-400 focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-400/20 text-sm sm:text-base"
                  />
                </div>

                <div>
                  <label className="block text-amber-400 font-semibold mb-2 flex items-center space-x-2">
                    <Cross className="w-4 h-4" />
                    <span>Cererea ta de rugăciune:</span>
                  </label>
              
                  <textarea
                    name="message"
                    rows="4"
                    className="w-full p-3 sm:p-4 bg-slate-800/70 border border-amber-400/30 rounded-xl text-white placeholder-slate-400 focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-400/20 text-sm sm:text-base"
                    placeholder="Împărtășește-ți cererea de rugăciune aici..."
                    required
                  />
                </div>
              
                <button
                  type="submit"
                  className="w-full sm:w-auto bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-slate-900 font-bold py-3 px-6 sm:px-8 rounded-xl transition-all duration-300 transform hover:scale-105 flex items-center justify-center space-x-2"
                >
                  <Send className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span>Trimite Cererea</span>
                </button>
              </form>

            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 60 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative order-1 lg:order-2"
            >
              <img 
                src="https://images.unsplash.com/photo-1504052434569-70ad5836ab65"
                alt="Rugăciune și Meditație"
                className="w-full h-64 sm:h-80 lg:h-96 object-cover rounded-3xl shadow-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/30 to-transparent rounded-3xl"></div>
              <div className="absolute top-4 right-4">
                <Bird className="w-8 h-8 text-amber-400" />
              </div>
              <div className="absolute bottom-4 left-4">
                <Cross className="w-6 h-6 text-amber-400" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-r from-amber-500/10 to-amber-600/10 relative">
        {/* Decorative star pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="grid grid-cols-10 gap-12 p-8">
            {[...Array(50)].map((_, i) => (
              <Star key={i} className="w-3 h-3 text-amber-400" />
            ))}
          </div>
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 text-center relative">
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex justify-center items-center space-x-3 mb-4 sm:mb-6">
              <Star className="w-6 h-6 sm:w-8 sm:h-8 text-amber-400" />
              <h2 className="text-3xl sm:text-4xl font-bold text-amber-400">Rămâi Conectat</h2>
              <Star className="w-6 h-6 sm:w-8 sm:h-8 text-amber-400" />
            </div>
            <p className="text-lg sm:text-xl text-slate-300 mb-6 sm:mb-8 max-w-2xl mx-auto px-4">
              Primește notificări despre noile episoade și studii biblice direct în inbox-ul tău
            </p>
            
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                const email = formData.get('email');
                const nume = formData.get('nume');
                const prenume = formData.get('prenume');
                
                // Mostra stato di caricamento
                const submitBtn = e.target.querySelector('button[type="submit"]');
                const originalText = submitBtn.innerHTML;
                submitBtn.innerHTML = '<svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>Se procesează...';
                submitBtn.disabled = true;
                
                try {
                  const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/mailgun/newsletter/subscribe`, {
                    method: 'POST',
                    headers: { 
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ 
                      email: email,
                      nume: nume,
                      prenume: prenume
                    }),
                  });

                  const result = await response.json();
                  
                  if (response.ok && result.success) {
                    // Messaggio di successo migliorato
                    const successMessage = result.sandbox_mode 
                      ? '✅ Mulțumim pentru înscriere! În versiunea finală vei primi un email de confirmare. Pentru acum, te vom anunța despre episoadele noi prin alte canale. 📧'
                      : (result.message || 'Te-ai înscris cu succes la newsletter! Verifică-ți email-ul pentru confirmarea de bun venit.');
                    
                    alert(successMessage);
                    e.target.reset();
                  } else {
                    // Messaggio di errore migliorato  
                    const errorMessage = result.detail?.includes('sandbox') || result.message?.includes('sandbox')
                      ? '📝 Înregistrarea ta a fost salvată! Momentan sistemul este în modul de testare, dar te vom include în lista noastră. Mulțumim pentru înțelegere!'
                      : 'A apărut o problemă la înregistrare. Te rugăm să încerci din nou peste câteva minute.';
                    
                    alert(errorMessage);
                  }
                } catch (error) {
                  console.error('Newsletter subscription error:', error);
                  alert('📡 Problemă de conexiune. Te rugăm să încerci din nou peste câteva momente.');
                } finally {
                  // Ripristina il pulsante
                  submitBtn.innerHTML = originalText;
                  submitBtn.disabled = false;
                }
              }}
              className="space-y-4 max-w-md mx-auto"
            >
              <div className="flex flex-col sm:flex-row gap-3">
                <input 
                  name="nume"
                  type="text" 
                  placeholder="Nume"
                  className="flex-1 px-4 py-3 bg-slate-800/70 border border-amber-400/30 rounded-xl text-white placeholder-slate-400 focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-400/20"
                />
                <input 
                  name="prenume"
                  type="text" 
                  placeholder="Prenume"
                  className="flex-1 px-4 py-3 bg-slate-800/70 border border-amber-400/30 rounded-xl text-white placeholder-slate-400 focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-400/20"
                />
              </div>
              <input 
                name="email"
                type="email" 
                required 
                placeholder="Adresa ta de email"
                className="w-full px-4 py-3 bg-slate-800/70 border border-amber-400/30 rounded-xl text-white placeholder-slate-400 focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-400/20"
              />
              <button 
                type="submit"
                className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-slate-900 font-bold py-3 px-6 rounded-xl transition-all duration-300 transform hover:scale-105 flex items-center justify-center space-x-2"
              >
                <Send className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>Înscrie-te</span>
              </button>
            </form>


          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 sm:py-20 bg-slate-800/50 relative">
        {/* Decorative shield pattern */}
        <div className="absolute top-6 left-6 text-amber-400/10">
          <Shield className="w-16 h-16" />
        </div>
        <div className="absolute bottom-6 right-6 text-amber-400/10">
          <Crown className="w-20 h-20" />
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 relative">
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12 sm:mb-16"
          >
            <div className="flex justify-center items-center space-x-3 mb-4 sm:mb-6">
              <Heart className="w-6 h-6 sm:w-8 sm:h-8 text-amber-400" />
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-amber-400">Contact și Comunitate</h2>
              <Heart className="w-6 h-6 sm:w-8 sm:h-8 text-amber-400" />
            </div>
            <p className="text-lg sm:text-xl text-slate-300 max-w-3xl mx-auto px-4">
              Intră în legătură cu noi și fii parte din comunitatea noastră de credință
            </p>
          </motion.div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center p-6 sm:p-8 bg-slate-700/50 rounded-3xl border border-amber-400/20"
            >
              <div className="flex justify-center items-center space-x-2 mb-4">
                <Youtube className="w-8 h-8 sm:w-12 sm:h-12 text-amber-400" />
                <Star className="w-4 h-4 text-amber-300" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-amber-400 mb-3">YouTube</h3>
              <p className="text-sm sm:text-base text-slate-300 mb-4">
                Urmărește-ne pe YouTube pentru episoadele video și live streams
              </p>
              <button onClick={() => window.open('https://www.youtube.com/@mircea_stanciu', '_blank')} className="text-amber-400 hover:text-amber-300 transition-colors font-semibold text-sm sm:text-base">
                Vizitează Canalul →
              </button>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-center p-6 sm:p-8 bg-slate-700/50 rounded-3xl border border-amber-400/20"
            >
              <div className="flex justify-center items-center space-x-2 mb-4">
                <Mail className="w-8 h-8 sm:w-12 sm:h-12 text-amber-400" />
                <Feather className="w-4 h-4 text-amber-300" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-amber-400 mb-3">Email</h3>
              <p className="text-sm sm:text-base text-slate-300 mb-4">
                Trimite-ne un email pentru întrebări sau sugestii
              </p>
              <a href="mailto:virgilstaciu31@gmail.com">
                <button
                  className="text-amber-400 hover:text-amber-300 transition-colors font-semibold text-sm sm:text-base"
                >
                  Scrie-ne →
                </button>
              </a>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-center p-6 sm:p-8 bg-slate-700/50 rounded-3xl border border-amber-400/20 sm:col-span-2 lg:col-span-1"
            >
              <div className="flex justify-center items-center space-x-2 mb-4">
                <Heart className="w-8 h-8 sm:w-12 sm:h-12 text-amber-400" />
                <Cross className="w-4 h-4 text-amber-300" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-amber-400 mb-3">Comunitate</h3>
              <p className="text-sm sm:text-base text-slate-300 mb-4">
                Alătură-te comunității noastre pentru discuții și rugăciune
              </p>
              <button
                onClick={() => window.open('https://chat.whatsapp.com/DqlrnJxLjzwHKtsSAgGbaJ', '_blank')}
                className="text-amber-400 hover:text-amber-300 transition-colors font-semibold text-sm sm:text-base"
              >
                Alătură-te →
              </button>

            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default LandingPage;